import React from "react";
import { motion } from "framer-motion";
import { AmbientLight } from "@/components/brand/AmbientLight";
import ChapterMark from "@/components/brand/ChapterMark";

const STAGES = [
  {
    n: "01",
    title: "Intake",
    body:
      "Policy review, loss assessment, and engagement. The firm establishes scope and timeline before any contact with the carrier.",
  },
  {
    n: "02",
    title: "Investigation",
    body:
      "On-site inspection, photographic documentation, sampling, and engineering coordination where the loss requires it.",
  },
  {
    n: "03",
    title: "Documentation",
    body:
      "Scope of damage, line-item estimate, code-compliance review, and the evidentiary file supporting the claim.",
  },
  {
    n: "04",
    title: "Negotiation",
    body:
      "Carrier presentation, supplements, re-inspections, and the structured back-and-forth that drives recovery.",
  },
  {
    n: "05",
    title: "Recovery",
    body:
      "Settlement, payment release, and — when necessary — reopening procedures or referral to litigation counsel.",
  },
];

function Station({ n }) {
  return (
    <div className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 flex items-center justify-center flex-shrink-0">
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 50% 50%, rgba(198,168,107,0.18), rgba(0,119,255,0.06) 50%, transparent 75%)",
          filter: "blur(2px)",
        }}
      />
      <div
        className="absolute inset-2 rounded-full border border-gold/25"
        style={{
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.06), 0 18px 36px -10px rgba(0,0,0,0.55)",
        }}
      />
      <div className="absolute inset-[18%] rounded-full border border-white/5" />
      <span className="relative font-display-tight gold-metallic text-xl sm:text-2xl lg:text-3xl font-light tracking-[-0.04em]">
        {n}
      </span>
    </div>
  );
}

export default function AGMethod() {
  return (
    <section
      data-testid="ag-method-section"
      className="relative py-24 sm:py-32 lg:py-52 overflow-hidden bg-midnight-deeper"
    >
      <AmbientLight size={1200} color="gold" intensity={0.3} style={{ top: "20%", left: "50%", transform: "translateX(-50%)" }} />
      <AmbientLight size={800} color="blue" intensity={0.3} style={{ bottom: "5%", left: "-10%" }} />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12">
        <ChapterMark index="05" label="The AG Method" align="center" className="mb-14" />

        <div className="text-center max-w-4xl mx-auto mb-28">
          <h2 className="font-display-tight text-white text-[clamp(2.5rem,5.6vw,5.25rem)] leading-[0.96] tracking-[-0.045em]">
            A five-stage system,
            <br />
            <span className="gold-metallic font-display font-light">applied to every claim.</span>
          </h2>
          <div className="mx-auto mt-12 h-px w-20 bg-gradient-to-r from-transparent via-gold to-transparent" />
          <p className="mt-12 text-ag-text-secondary text-base lg:text-[1.1rem] leading-[1.75] font-light max-w-2xl mx-auto">
            AG Public Adjusting operates the same disciplined sequence on every file. The method
            is what replaces personality — and what produces consistent recoveries.
          </p>
        </div>

        {/* Desktop horizontal timeline */}
        <div className="hidden lg:block relative">
          {/* connecting rail */}
          <div
            className="absolute top-[3.85rem] left-[6%] right-[6%] h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(198,168,107,0.55) 12%, rgba(0,170,255,0.55) 50%, rgba(198,168,107,0.55) 88%, transparent 100%)",
            }}
          />
          {/* travelling light */}
          <motion.div
            initial={{ x: "0%", opacity: 0 }}
            whileInView={{ x: "100%", opacity: [0, 1, 0] }}
            viewport={{ once: true }}
            transition={{ duration: 3.2, ease: "easeInOut", delay: 0.4 }}
            className="absolute top-[3.85rem] left-[6%] w-32 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(244,228,188,1), rgba(0,170,255,0.6), transparent)",
              filter: "blur(1px)",
            }}
          />
          <div className="grid grid-cols-5 gap-6">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.14 }}
                className="relative flex flex-col items-center text-center"
                data-testid={`ag-method-stage-${i + 1}`}
              >
                <Station n={s.n} />
                <h3 className="mt-8 font-display text-xl lg:text-[1.6rem] text-white font-light tracking-[-0.025em]">
                  {s.title}
                </h3>
                <div className="mt-3 h-px w-8 bg-gold/40" />
                <p className="mt-5 text-ag-text-secondary text-[0.9rem] lg:text-[0.95rem] font-light leading-[1.7] max-w-[18ch]">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mobile vertical */}
        <div className="lg:hidden space-y-8 sm:space-y-10 max-w-xl mx-auto">
          {STAGES.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex gap-5 sm:gap-6"
              data-testid={`ag-method-stage-mobile-${i + 1}`}
            >
              <div className="flex flex-col items-center">
                <Station n={s.n} />
                {i < STAGES.length - 1 && (
                  <div className="w-px flex-1 bg-gradient-to-b from-gold/40 to-glow/15 my-3" />
                )}
              </div>
              <div className="pt-2 pb-2 flex-1 min-w-0">
                <h3 className="font-display text-xl sm:text-2xl text-white mb-2 sm:mb-3 font-light tracking-[-0.025em]">{s.title}</h3>
                <p className="text-ag-text-secondary text-[0.92rem] sm:text-sm leading-[1.7] font-light">{s.body}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Closing principle */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, delay: 0.4 }}
          className="mt-28 lg:mt-32 text-center"
        >
          <div className="mx-auto h-px w-12 bg-gold/60 mb-8" />
          <p className="font-display text-lg lg:text-2xl text-white/80 font-extralight tracking-[-0.02em] max-w-3xl mx-auto leading-[1.45]">
            The method is the firm. The firm is the method.
          </p>
          <p className="mt-5 font-mono text-[0.62rem] text-ag-text-muted tracking-[0.32em] uppercase">
            AG Public Adjusting · Operating Doctrine
          </p>
        </motion.div>
      </div>
    </section>
  );
}
